import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import TableContainer from './components/TableContainer';
import Pagination10 from './components/Pagination10';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ResponsiveShadow from './components/ResponsiveShadow';
import usePagination10 from './components/hooks/usePagination10';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import ArpModal from './components/modals/ArpModal';
import ArpItensModal from './components/modals/ArpItensModal';
import ArpOrdensModal from './components/modals/ArpOrdensModal';
import DataTable from './components/DataTable';

function Arps() {
  const [arps, setArps] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [atualizar, setAtualizar] = useState(false);
  const [arpSelecionada, setArpSelecionada] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [arpStats, setArpStats] = useState({});
  const [arpStatus, setArpStatus] = useState({});
  const token = localStorage.getItem('token');
  const [isLoading, setLoading] = useState(true);
  const [tableMaxHeight, setTableMaxHeight] = useState('calc(100vh - 130px)');
  const clampLines = 3;
  const [selectedStatus, setSelectedStatus] = useState('');
  const location = useLocation();
  const selectStatusRef = useRef(null);
  const [highlight, setHighlight] = useState(false);
  const [modalItensAberto, setModalItensAberto] = useState(false);
  const [modalOrdensAberto, setModalOrdensAberto] = useState(false);
  const navigate = useNavigate();
  const vinculo = JSON.parse(localStorage.getItem('vinculo'));
  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin = user?.role === 'ADMIN';
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const anoBase = parseInt(localStorage.getItem('anoBase'));

  const importFullJsonInputRef = useRef(null);
  const importInputRef = useRef(null);
  const saldoPollRef = useRef(null);

  async function handleImportFullJson(e) {
    const file = e.target.files[0];
    if (!importFullJsonInputRef.current) return;
    importFullJsonInputRef.current.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async event => {
      let data;
      try {
        data = JSON.parse(event.target.result);
      } catch {
        showDefaultModal('error', 'Arquivo JSON inválido.');
        return;
      }
      if (!Array.isArray(data) || data.length === 0) {
        showDefaultModal('error', 'O JSON deve ser um array não vazio.');
        return;
      }

      const parseDateDMY = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const [d, m, y] = parts;
          return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
        }
        return dateStr;
      };

      setLoading(true);
      let totalItens = 0, totalOrdens = 0, erroItens = 0, erroOrdens = 0;
      const arpsNaoEncontradas = [];
      for (const bloco of data) {
        // 1. Encontrar ARP pelo numArp e ano
        let arpId = null;
        const numArp = bloco.arp?.numArp;
        const partes = (numArp || '').split('/');
        const anoArp = partes.length >= 2 ? partes[partes.length - 1].replace(/\D/g, '').trim() : anoBase;
        const numPart = partes[0].trim();
        try {
          const res = await axios.get(`${API_BASE_URL.ARP}/entidade/${vinculo.idEntidade}/ano/${anoArp}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const encontrado = (res.data || []).find(a =>
            a.numArp === numArp ||
            (a.numArp === numPart && String(a.ano) === String(anoArp)) ||
            (a.numArp === `${numPart}/${anoArp}`)
          );
          arpId = encontrado?.id ?? null;
          if (!arpId) {
            // Se não encontrar, tenta criar a ARP
            const payload = {
              ...bloco.arp,
              ano: anoArp,
              idEntidade: vinculo.idEntidade,
              usuario: user?.login || '',
              dataInicio: parseDateDMY(bloco.arp?.dataInicio),
              dataFim: parseDateDMY(bloco.arp?.dataFim),
            };
            try {
              const resp = await axios.post(API_BASE_URL.ARP, payload, {
                headers: { Authorization: `Bearer ${token}` },
              });
              arpId = resp.data?.id;
            } catch (err) {
              arpsNaoEncontradas.push(numArp);
              continue;
            }
          }
        } catch (err) {
          arpsNaoEncontradas.push(numArp);
          continue;
        }
        // 2. Importar itens
        if (Array.isArray(bloco.itens)) {
          for (const item of bloco.itens) {
            const itemPayload = {
              id: item.id,
              idArp: arpId,
              descricao: item.descricao,
              marca: item.marca,
              unidade: item.unidade,
              quantidade: item.quantidade,
              valorUnit: item.valorUnit,
            };
            try {
              await axios.post(API_BASE_URL.ARP_ITEM, itemPayload, { headers: { Authorization: `Bearer ${token}` } });
              totalItens++;
            } catch {
              erroItens++;
            }
          }
        }
        // 3. Importar ordens
        if (Array.isArray(bloco.ordens)) {
          for (const ordemObj of bloco.ordens) {
            const ordem = ordemObj.ordem;
            try {
              const ordemPayload = {
                id: ordem.id,
                idArp: arpId,
                data: parseDateDMY(ordem.data),
                valorTotal: ordem.valorTotal,
              };
              const ordemResp = await axios.post(API_BASE_URL.ARP_ORDEM, ordemPayload, {
                headers: { Authorization: `Bearer ${token}` },
              });
              const ordemId = ordemResp.data?.id || ordem.id;
              if (Array.isArray(ordemObj.itens)) {
                for (const i of ordemObj.itens) {
                  const itemPayload = {
                    idArp: arpId,
                    idArpOrdem: ordemId,
                    idArpItem: i.idArpItem,
                    qtdeAcumAnterior: i.qtdeAcumAnterior,
                    qtdePedida: i.qtdePedida,
                    qtdeAcumAtual: i.qtdeAcumAtual,
                    descricaoItem: i.descricaoItem,
                    marcaItem: i.marcaItem,
                    unidadeItem: i.unidadeItem,
                    quantidadeContratada: i.quantidadeContratada,
                    valorUnitario: i.valorUnitario,
                    valorTotalItem: i.valorTotalItem,
                  };
                  await axios.post(API_BASE_URL.ARP_ORDEM_ITEM, itemPayload, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                }
              }
              totalOrdens++;
            } catch (err) {
              erroOrdens++;
            }
          }
        }
      }
      setLoading(false);
      setAtualizar(prev => !prev);
      let msg = `${totalItens} item(ns) e ${totalOrdens} ordem(ns) importados com sucesso.`;
      if (erroItens || erroOrdens) msg += ` ${erroItens} erro(s) em itens, ${erroOrdens} erro(s) em ordens.`;
      if (arpsNaoEncontradas.length > 0) {
        msg += ` ARPs não encontradas (${arpsNaoEncontradas.length}): ${arpsNaoEncontradas.join(', ')}.`;
      }
      const temErro = erroItens > 0 || erroOrdens > 0 || arpsNaoEncontradas.length > 0;
      showDefaultModal(temErro ? 'error' : 'success', msg);
    };
    reader.readAsText(file);
  }

  function handleImportJson(event) {
    if (!isAdmin) return;
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    function parseDateBRtoISO(dateStr) {
      if (!dateStr) return '';
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const [d, m, y] = parts;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }
      return dateStr;
    }
    reader.onload = async (e) => {
      let data;
      try {
        data = JSON.parse(e.target.result);
      } catch {
        showDefaultModal('error', 'Arquivo JSON inválido.');
        return;
      }
      if (!Array.isArray(data) || data.length === 0) {
        showDefaultModal('error', 'O JSON deve ser um array não vazio.');
        return;
      }
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;
      for (const item of data) {
        try {
          const ano = parseInt((item.numArp || '').split('/')[1]) || new Date().getFullYear();
          const payload = {
            ...item,
            ano,
            idEntidade: vinculo.idEntidade,
            usuario: user?.login || '',
            dataInicio: parseDateBRtoISO(item.dataInicio),
            dataFim: parseDateBRtoISO(item.dataFim),
          };
          await axios.post(API_BASE_URL.ARP, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });
          successCount++;
        } catch (e) {
          errorCount++;
        }
      }
      setLoading(false);
      setAtualizar(prev => !prev);
      if (errorCount === 0) {
        showDefaultModal('success', `${successCount} ARP(s) importada(s) com sucesso!`);
      } else {
        showDefaultModal('error', `${successCount} importada(s) com sucesso, ${errorCount} com erro.`);
      }
      event.target.value = '';
    };
    reader.readAsText(file);
  }

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    if (!vinculo || !vinculo.idEntidade) {
      showDefaultModal('error', 'Selecione uma entidade antes de acessar esta página.');
      window.location.href = '/home';
      return;
    }
    // Aplicar filtro vindo de query params (ex: clique no card do dashboard)
    try {
      const params = new URLSearchParams(location.search);
      if (params.has('status')) {
        const s = params.get('status') || '';
        setSelectedStatus(s);
        setHighlight(true);
        // foco no select de filtro para indicar o contexto
        setTimeout(() => {
          try { selectStatusRef.current && selectStatusRef.current.focus(); } catch (e) {}
        }, 120);
        // remover highlight após animação
        setTimeout(() => setHighlight(false), 900);
        // limpar querystring para evitar reaplicação em refresh (mantém URL limpa)
        try { window.history.replaceState({}, document.title, window.location.pathname); } catch (e) {}
      }
    } catch (e) {
      // ignore
    }
    setLoading(true);
    axios
      .get(`${API_BASE_URL.ARP}/entidade/${vinculo.idEntidade}/ano/${anoBase}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setArps(result.data);
        carregarEstatisticas(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter ARPs:', error);
        showDefaultModal('error', 'Erro ao carregar ARPs.');
      })
      .finally(() => setLoading(false));
  }, [atualizar]);

  useEffect(() => {
    return () => {
      if (saldoPollRef.current) {
        clearInterval(saldoPollRef.current);
        saldoPollRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    function computeTableMaxHeight() {
      try {
        const header = document.querySelector('.navbar');
        const footer = document.querySelector('footer.footer') || document.querySelector('footer');
        const headerBottom = header ? header.getBoundingClientRect().bottom : 80;
        const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight - 40;
        const available = Math.floor(footerTop - headerBottom - 40);
        setTableMaxHeight(`${Math.max(200, available)}px`);
      } catch (e) {
        setTableMaxHeight('calc(100vh - 130px)');
      }
    }

    computeTableMaxHeight();
    window.addEventListener('resize', computeTableMaxHeight);
    const mo = new MutationObserver(computeTableMaxHeight);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true });
    return () => {
      window.removeEventListener('resize', computeTableMaxHeight);
      mo.disconnect();
    };
  }, []);

  function carregarEstatisticas(arpsLista) {
    // Usar endpoint consolidado para reduzir número de requisições
    const reqStats = axios.get(`${API_BASE_URL.ARP}/stats`, { params: { idEntidade: vinculo.idEntidade, anoBase }, headers: { Authorization: `Bearer ${token}` } });
    const reqStatus = axios.get(`${API_BASE_URL.ARP}/status`, { params: { idEntidade: vinculo.idEntidade, anoBase }, headers: { Authorization: `Bearer ${token}` } });
    Promise.all([reqStats, reqStatus])
      .then(([resStats, resStatus]) => {
        setArpStats(resStats.data || {});
        setArpStatus(resStatus.data || {});
      })
      .catch(err => {
        console.error('Erro ao carregar estatísticas ou status consolidados:', err);
        setArpStats({});
        setArpStatus({});
      });
  }

  function handleDelete(arp) {
    showConfirmModal('delete', () => handleConfirmDelete(arp));
  }

  function handleConfirmDelete(arp) {
    axios
      .delete(`${API_BASE_URL.ARP}/${arp.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        showDefaultModal('success', 'ARP excluída com sucesso!');
        setAtualizar(prev => !prev);
        setArpSelecionada(null);
      })
      .catch(error => {
        console.error('Erro ao excluir ARP:', error);
        showDefaultModal('error', 'Erro ao excluir a ARP!');
      });
  }

  function handleEdit(arp) {
    setArpSelecionada(arp);
    setShowModal(true);
  }

  function handleNew() {
    setArpSelecionada(null);
    setShowModal(true);
  }

  function handleSaldo(arp) {
    if (!arp) return;
    if (saldoPollRef.current) {
      clearInterval(saldoPollRef.current);
      saldoPollRef.current = null;
    }
    setLoading(true);
    axios
      .get(`${API_BASE_URL.ARP}/saldoOrdensPdf/${arp.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        const { jobId } = res.data || {};
        if (!jobId) {
          throw new Error('Sem jobId na resposta');
        }
        pollSaldo(jobId, arp);
      })
      .catch(err => {
        console.error('Erro ao gerar saldo de ordens:', err);
        setLoading(false);
        showDefaultModal('error', 'Erro ao gerar relatório de saldo de ordens.');
      });
  }

  function pollSaldo(jobId, arp) {
    const poll = () => {
      axios
        .get(`${API_BASE_URL.ARP}/saldoOrdensPdfStatus/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(res => {
          const { status, message } = res.data || {};
          if (status === 'PRONTO') {
            stopSaldoPoll();
            fetchSaldoFile(jobId, arp);
          } else if (status === 'ERRO' || status === 'CHEIO') {
            stopSaldoPoll();
            setLoading(false);
            showDefaultModal('error', message || 'Erro ao gerar relatório de saldo de ordens.');
          }
        })
        .catch(err => {
          stopSaldoPoll();
          console.error('Erro ao verificar status do relatório:', err);
          setLoading(false);
          showDefaultModal('error', 'Erro ao verificar status do relatório.');
        });
    };
    stopSaldoPoll();
    poll();
    saldoPollRef.current = setInterval(poll, 2000);
  }

  function stopSaldoPoll() {
    if (saldoPollRef.current) {
      clearInterval(saldoPollRef.current);
      saldoPollRef.current = null;
    }
  }

  function fetchSaldoFile(jobId, arp) {
    axios
      .get(`${API_BASE_URL.ARP}/saldoOrdensPdfFile/${jobId}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        setLoading(false);
        if (!res.data || res.data.size === 0) {
          throw new Error('PDF vazio');
        }
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const pdfId = `saldo_arp_${arp.id}_${Date.now()}`;

        if (!window.pdfStorage) {
          window.pdfStorage = {};
        }
        window.pdfStorage[pdfId] = blob;

        sessionStorage.setItem(
          'pdfViewerData',
          JSON.stringify({ url, title: `Saldo Ordens - ARP ${arp.numArp}`, pdfId })
        );

        window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));
      })
      .catch(err => {
        setLoading(false);
        console.error('Erro ao baixar o relatório:', err);
        showDefaultModal('error', 'Erro ao baixar o relatório gerado.');
      });
  }

  function handleCloseModal() {
    setShowModal(false);
    setArpSelecionada(null);
  }

  function handleSaveSuccess() {
    setAtualizar(prev => !prev);
    setArpSelecionada(null);
  }

  const normalizeString = str =>
    str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

  const arpsFiltradas = Array.isArray(arps)
    ? arps
        .filter(a => {
          const b = normalizeString(filtro);
          const st = arpStatus[a.id];
          if (selectedStatus) {
            if (selectedStatus === 'VENCE') {
              if (!(st && typeof st === 'string' && st.startsWith('VENCE_EM_'))) return false;
            } else if (selectedStatus === 'ATIVO') {
              if (st && (st === 'ENCERRADO' || (typeof st === 'string' && st.startsWith('VENCE_EM_')))) return false;
            } else {
              if (st !== selectedStatus) return false;
            }
          }
          return (
            normalizeString(a.numArp || '').includes(b) ||
            normalizeString(a.objeto || '').includes(b) ||
            normalizeString(a.fornecedor?.nome || '').includes(b) ||
            normalizeString(a.numProcesso || '').includes(b)
          );
        })
        .sort((a, b) => (b.numArp || '').localeCompare(a.numArp || '', undefined, { numeric: true }))
    : [];

  const totalItems = arpsFiltradas.length;
  const { setPagination } = usePagination10(totalItems);
  const paginationRef = useRef();
  paginationRef.current = setPagination;
  const currentItems = arpsFiltradas.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  useEffect(() => {
    if (paginationRef.current && paginationRef.current.resetPage) {
      paginationRef.current.resetPage();
    }
  }, [filtro, selectedStatus]);

  const formatDate = date => {
    if (!date) return '';
    if (Array.isArray(date)) {
      const [year, month, day] = date;
      return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    }
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="ARPs - Atas de Registro de Preços" icon="bi-journal-text" />
        <PageContent>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between align-items-start align-items-lg-center gap-2 mb-2">
            <div className="botao-grid-2col">
              <button className="btn btn-success btn-sm botao-igual" onClick={handleNew} title="Nova ARP">
                <i className="bi bi-plus-circle me-1"></i>Nova
              </button>
              <button
                className="btn btn-primary btn-sm botao-igual"
                disabled={!arpSelecionada}
                onClick={() => handleEdit(arpSelecionada)}
                title={!arpSelecionada ? 'Selecione uma ARP' : 'Editar ARP selecionada'}
              >
                <i className="bi bi-pencil me-1"></i>Editar
              </button>
              <button
                className="btn btn-danger btn-sm botao-igual"
                disabled={!arpSelecionada}
                onClick={() => handleDelete(arpSelecionada)}
                title={!arpSelecionada ? 'Selecione uma ARP' : 'Excluir ARP selecionada'}
              >
                <i className="bi bi-trash me-1"></i>Excluir
              </button>
              <div className="toolbar-separator" />
              <button
                className="btn btn-info btn-sm text-white botao-igual"
                disabled={!arpSelecionada}
                onClick={() => setModalItensAberto(true)}
                title={arpSelecionada?.possuiAditivo ? "Visualizar itens (edição apenas no aditivo)" : "Ver itens da ARP selecionada"}
              >
                <i className="bi bi-list-ul me-1"></i>Itens
              </button>
              <button
                className="btn btn-secondary btn-sm botao-igual"
                disabled={!arpSelecionada}
                onClick={() => setModalOrdensAberto(true)}
                title={arpSelecionada?.possuiAditivo ? "Visualizar ordens (registro apenas no aditivo)" : "Ver ordens de fornecimento da ARP"}
              >
                <i className="bi bi-truck me-1"></i>Ordens
              </button>
              <button
                className="btn btn-outline-primary btn-sm botao-igual"
                disabled={!arpSelecionada}
                onClick={() => handleSaldo(arpSelecionada)}
                title="Gerar relatório de saldo de ordens"
              >
                <i className="bi bi-file-earmark-text me-1"></i>Saldo Ordens
              </button>
              {isAdmin && (
                <>
                  <div className="btn-group">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm dropdown-toggle botao-igual"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      title="Importar"
                    >
                      <i className="bi bi-file-earmark-arrow-up me-1"></i>
                      Import
                    </button>
                    <ul className="dropdown-menu">
                      <li>
                        <button className="dropdown-item" type="button" onClick={() => importInputRef.current?.click()}>
                          Import JSON
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" type="button" onClick={() => importFullJsonInputRef.current?.click()}>
                          Import JSON Completo
                        </button>
                      </li>
                    </ul>
                  </div>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept=".json,application/json"
                    style={{ display: 'none' }}
                    onChange={handleImportJson}
                  />
                  <input
                    ref={importFullJsonInputRef}
                    type="file"
                    accept=".json,application/json"
                    style={{ display: 'none' }}
                    onChange={handleImportFullJson}
                  />
                </>
              )}
            </div>
            <div className="d-flex align-items-center gap-2 flex-nowrap mt-2 mt-lg-0" style={{ transition: 'box-shadow 0.35s ease', boxShadow: highlight ? '0 0 0 6px rgba(13,110,253,0.18)' : 'none' }}>
              <div className="input-group input-group-sm" style={{ minWidth: 100 }}>
                <span className="input-group-text d-flex align-items-center justify-content-center"><i className="bi bi-filter"></i></span>
                <select ref={selectStatusRef} className="form-select form-select-sm" style={{ width: '140px' }} value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
                  <option value="">Todos</option>
                  <option value="ATIVO">Ativo</option>
                  <option value="ENCERRADO">Encerrado</option>
                  <option value="VENCE">Vence em...</option>
                </select>
              </div>
              <div className="input-group input-group-sm" style={{ minWidth: 150 }}>
                <span className="input-group-text d-flex align-items-center justify-content-center"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Buscar..."
                  title="Buscar por número, objeto, fornecedor..."
                  value={filtro}
                  onChange={e => setFiltro(e.target.value.toUpperCase())}
                />
              </div>
            </div>
          </div>

          <TableContainer isLoading={isLoading}>
              <DataTable style={{ fontSize: '0.8rem' }} head={
                <tr>
                  <th scope="col" className="px-2">Nº ARP</th>
                  <th scope="col" className="px-2">Fornecedor</th>
                  <th scope="col" className="px-2" style={{ minWidth: '180px' }}>Vigência</th>
                  <th scope="col" className="px-2 text-center">Status</th>
                  <th scope="col" className="px-2 text-center">Itens</th>
                  <th scope="col" className="px-2 text-center">Ordens</th>
                </tr>}>
            
              <tbody>
                {currentItems.map(arp => (
                    <tr
                      key={arp.id}
                      className={arpSelecionada && arpSelecionada.id === arp.id ? 'table-primary' : ''}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setArpSelecionada(arp)}
                      onDoubleClick={() => { setArpSelecionada(arp); setModalItensAberto(true); }}
                    >
                      <td className="px-2 fw-semibold">
                        {arp.numArp}
                        {arp.tipoCadastro === 'ADITIVO' && (
                          <span className="badge bg-info text-dark ms-1" style={{ fontSize: '0.65rem' }}>
                            ADITIVO
                          </span>
                        )}
                      </td>
                      {/* coluna ano removida */}
                      <td className="px-2">{arp.fornecedor?.nome || arp.idFornecedor}</td>
                      <td className="px-2" style={{ minWidth: '180px' }}>
                        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: clampLines, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {formatDate(arp.dataInicio)}
                          {arp.dataFim && <> – {formatDate(arp.dataFim)}</>}
                        </div>
                      </td>
                      <td className="px-2 text-center">
                        {(() => {
                          const st = arpStatus[arp.id];
                          if (!st) return <span className="badge bg-secondary">...</span>;
                          if (st === 'ENCERRADO') return <span className="badge bg-danger">Encerrado</span>;
                          if (st && st.startsWith('VENCE_EM_')) {
                            const m = st.match(/VENCE_EM_(\d+)_DIAS/);
                            const dias = m ? Number(m[1]) : NaN;
                            if (!isNaN(dias)) {
                              if (dias === 0) return <span className="badge bg-warning text-dark">Vence hoje</span>;
                              if (dias === 1) return <span className="badge bg-warning text-dark">Vence amanhã</span>;
                              return <span className="badge bg-warning text-dark">Vence em {dias} dias</span>;
                            }
                            return <span className="badge bg-warning text-dark">Vence em ? dias</span>;
                          }
                          return <span className="badge bg-success">Ativo</span>;
                        })()}
                      </td>
                      <td className="px-2 text-center">
                        <span className="badge bg-secondary">{arpStats[arp.id]?.itens ?? '...'}</span>
                      </td>
                      <td className="px-2 text-center">
                        <span className="badge bg-info text-dark">{arpStats[arp.id]?.ordens ?? '...'}</span>
                      </td>
                    </tr>
                  ))}
                {currentItems.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center py-3 text-muted">
                      <i className="bi bi-inbox fs-3 d-block mb-2"></i>
                      Nenhuma ARP encontrada
                    </td>
                  </tr>
                )}
              </tbody>
              </DataTable>
            </TableContainer>

          {showModal && (
            <ArpModal show={showModal} arp={arpSelecionada} onClose={handleCloseModal} onSave={handleSaveSuccess} />
          )}
        </PageContent>
        <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid rgba(0,0,0,0.1)', backgroundColor: '#f8f9fa' }}>
          <Pagination10 {...setPagination} />
        </div>
        <ConfirmModal {...setConfirmModal} />
        <DefaultModal {...setDefaultModal} />
        <ArpItensModal isOpen={modalItensAberto} onClose={() => setModalItensAberto(false)} arp={arpSelecionada} possuiAditivo={arpSelecionada?.possuiAditivo} onSave={() => setAtualizar(prev => !prev)} />
        <ArpOrdensModal isOpen={modalOrdensAberto} onClose={() => { setModalOrdensAberto(false); setAtualizar(prev => !prev); }} arp={arpSelecionada} possuiAditivo={arpSelecionada?.possuiAditivo} />
      </ResponsiveShadow>
    </Layout>
  );
}

export default Arps;
